import { useBankAccount } from "@/composables/useBankAccount";
import { useI18n } from "@/composables/useI18n";
import { formatCurrency } from "@/utils/tools";
import { getGclBalance } from "@aircap/common/gcl";
import { computed, type Ref } from "vue";

export const useAccountCurrency = (accountId: Ref<string>) => {
  const { locale } = useI18n();
  const { accountType, bridgeAccount, swanAccount, powensAccount, gclAccount, treezorAccount } =
    useBankAccount(accountId);

  const currencyCode = computed<string | null>(() => {
    switch (accountType.value) {
      case undefined: {
        return null;
      }
      case "bridge": {
        return bridgeAccount.value?.details.currency_code ?? null;
      }
      case "swan": {
        return swanAccount.value?.currency ?? null;
      }
      case "powens": {
        return powensAccount.value?.currency?.id ?? null;
      }
      case "gcl": {
        return gclAccount.value?.details?.currency ?? null;
      }
      case "treezor": {
        return treezorAccount.value.wallet?.currency ?? null;
      }
    }
  });

  const currencySymbol = computed(() => {
    if (!currencyCode.value) return "";
    return (
      new Intl.NumberFormat(locale.value, {
        style: "currency",
        currency: currencyCode.value,
        currencyDisplay: "narrowSymbol",
      })
        .formatToParts(0)
        .find((part) => part.type === "currency")?.value || ""
    );
  });

  const formattedBalance = computed(() => {
    if (!currencyCode.value || balance.value === null) return null;
    return formatCurrency({
      value: balance.value,
      currencyCode: currencyCode.value,
      locale: locale.value,
    });
  });

  const balance = computed<number | null>(() => {
    switch (accountType.value) {
      case undefined: {
        return null;
      }
      case "bridge": {
        return bridgeAccount.value?.details?.balance ?? null;
      }
      case "powens": {
        return powensAccount.value?.balance ?? null;
      }
      case "gcl": {
        if (!gclAccount.value) return null;

        const balance = getGclBalance(gclAccount.value);
        return parseFloat(balance.balanceAmount.amount);
      }
      case "swan": {
        return swanAccount.value?.balances
          ? parseFloat(swanAccount.value.balances.booked.value)
          : null;
      }
      case "treezor": {
        return treezorAccount.value.wallet?.solde ?? null;
      }
    }
  });

  return {
    accountType,
    currencyCode,
    currencySymbol,
    balance,
    formattedBalance,
  };
};
